// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-control/MissionControlIntelligencePanel.jsx

import React, { useMemo, useState } from "react";
import { Button, StatusChip } from "../../design-system/components";

const PROMPTS = [
  "What changed?",
  "What needs attention now?",
  "Why does it matter?",
  "What should I focus on first?",
];

function normalizeRows(value) {
  return Array.isArray(value) ? value : [];
}

function formatConfidence(value) {
  const normalized = String(value || "unknown").trim().toLowerCase();

  if (normalized === "high") return "High confidence";
  if (normalized === "medium") return "Medium confidence";
  if (normalized === "low") return "Low confidence";
  return "Confidence unavailable";
}

function confidenceTone(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "high") return "success";
  if (normalized === "medium") return "informational";
  if (normalized === "low") return "warning";
  return "neutral";
}

function sourceLabel(source = {}) {
  const label = String(source?.item_label || "").trim();
  if (label) return label;

  return String(source?.id || source?.type || "OASYS source")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function sourceDetail(source = {}) {
  if (source?.type === "top_risk_reference") {
    return [source?.id, source?.resource_id, source?.control_id]
      .filter(Boolean)
      .join(" · ");
  }

  const itemCount = Number(source?.item_count);
  const records = Number(source?.records);

  if (Number.isFinite(itemCount) && itemCount > 0) {
    return `${itemCount} represented`;
  }

  if (Number.isFinite(records) && records > 0) {
    return `${records} loaded`;
  }

  return "Grounded";
}

export default function MissionControlIntelligencePanel({
  question = "What needs attention now?",
  card = null,
  metadata = null,
  loading = false,
  error = "",
  onAsk,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const recommendations = useMemo(
    () => normalizeRows(card?.recommended_actions).slice(0, 3),
    [card]
  );

  const evidence = useMemo(
    () =>
      normalizeRows(card?.evidence_used)
        .filter((item) => item && typeof item === "object")
        .slice(0, 12),
    [card]
  );

  const limitations = useMemo(
    () => normalizeRows(card?.known_limitations),
    [card]
  );

  const auditPersisted =
    metadata?.audit_persisted ??
    card?.metadata?.audit_persisted ??
    null;

  const handleAsk = (prompt) => {
    if (loading || typeof onAsk !== "function") return;
    onAsk(prompt);
  };

  return (
    <section
      className="mission-control-intelligence"
      aria-labelledby="mission-control-intelligence-title"
    >
      <div className="mission-control-intelligence__header">
        <div className="mission-control-intelligence__identity">
          <span className="mission-control-intelligence__eyebrow">
            OASYS Intelligence
          </span>
          <h2 id="mission-control-intelligence-title">
            Current operating intelligence
          </h2>
          <p>
            Grounded, advisory reasoning across Mission Control. Deterministic
            OASYS state remains authoritative.
          </p>
        </div>

        <div className="mission-control-intelligence__status">
          <StatusChip
            tone={confidenceTone(card?.confidence)}
            dot={Boolean(card)}
          >
            {loading
              ? "Reasoning…"
              : card
              ? formatConfidence(card.confidence)
              : "Awaiting context"}
          </StatusChip>

          {auditPersisted === true ? (
            <StatusChip tone="success">Audited</StatusChip>
          ) : null}

          <Button
            variant="ghost"
            size="small"
            onClick={() => setDetailsOpen((current) => !current)}
            aria-expanded={detailsOpen}
            aria-label={
              detailsOpen
                ? "Hide intelligence grounding details"
                : "Show intelligence grounding details"
            }
            className="mission-control-intelligence__trust-details"
          >
            {detailsOpen ? "Hide details" : "Details"}
          </Button>
        </div>
      </div>

      <div className="mission-control-intelligence__prompts">
        {PROMPTS.map((prompt) => (
          <Button
            key={prompt}
            variant={question === prompt ? "secondary" : "ghost"}
            size="small"
            onClick={() => handleAsk(prompt)}
            disabled={loading}
            className="mission-control-intelligence__prompt"
          >
            {prompt}
          </Button>
        ))}
      </div>

      <div
        className="mission-control-intelligence__body"
        aria-busy={loading}
      >
        {loading && !card ? (
          <div className="mission-control-intelligence__loading" role="status">
            <span className="mission-control-intelligence__pulse" />
            <div>
              <strong>Reading Mission Control truth…</strong>
              <span>
                Posture, risk, remediation, workflow, connector, and validation
                context are being assembled.
              </span>
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="mission-control-intelligence__error" role="alert">
            <strong>Intelligence unavailable</strong>
            <span>{error}</span>
          </div>
        ) : null}

        {card ? (
          <>
            <div className="mission-control-intelligence__reasoning">
              <span className="mission-control-intelligence__label">
                Current reasoning
              </span>
              <strong>{card.summary || "Mission Control context loaded."}</strong>
              {card.reasoning ? <p>{card.reasoning}</p> : null}
            </div>

            {recommendations.length > 0 ? (
              <div className="mission-control-intelligence__actions">
                <span className="mission-control-intelligence__label">
                  Recommended operator focus
                </span>
                <ol>
                  {recommendations.map((action, index) => (
                    <li key={`${String(action)}-${index}`}>
                      <span>{index + 1}</span>
                      <p>{String(action)}</p>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {detailsOpen ? (
        <div className="mission-control-intelligence__details">
          <div>
            <span className="mission-control-intelligence__label">
              Grounding
            </span>

            {evidence.length > 0 ? (
              <div className="mission-control-intelligence__sources">
                {evidence.map((source, index) => (
                  <div
                    key={`${source?.type || "source"}-${source?.id || index}`}
                    className="mission-control-intelligence__source"
                  >
                    <strong>{sourceLabel(source)}</strong>
                    <span>{sourceDetail(source)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mission-control-intelligence__muted">
                No source groups were returned for this response.
              </p>
            )}
          </div>

          <div>
            <span className="mission-control-intelligence__label">
              Known limitations
            </span>

            {limitations.length > 0 ? (
              <ul className="mission-control-intelligence__limitations">
                {limitations.map((limitation, index) => (
                  <li key={`${String(limitation)}-${index}`}>
                    {String(limitation)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mission-control-intelligence__muted">
                No additional limitations were reported.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
